import { Button, classNames, RefSelect } from '@fl/cmsch-fe-library';
import React, { FC, memo, RefObject } from 'react';
import { decimal52DecimalPlaces } from 'api/gen/Decimal52';
import { ApcReportFormValues } from './form-values';
import { useNewApcReportFormSetup } from '../../hooks/use-new-apc-report-form-setup';
import styles from './styles.sass';

/** Props for the analysis protocol correction form. */
interface Props {
  /** Ear-tag input ref used for focus orchestration. */
  earTagRef: RefObject<HTMLInputElement>;
  /** Technician number input ref used for focus orchestration. */
  technicianNumberRef: RefObject<HTMLInputElement>;
  /** Correction-type select ref used for focus orchestration. */
  correctionTypeRef: RefSelect;
  /** Submits the normalized form values. */
  onSubmit(formValues: ApcReportFormValues): void;
  /** Resets editing mode and clears the form. */
  onCancel(): void;
}

// eslint-disable-next-line max-lines-per-function
const ApcReportFormBase: FC<Props> = props => {
  const {
    earTagRef,
    correctionTypeRef,
    technicianNumberRef,
    onSubmit,
    onCancel
  } = props;
  const {
    Form,
    Fields,
    correctionTypeOptions,
    correctionType,
    editingCorrectionReport,
    proficiencyTestDateOptions,
    stableOptions,
    stableDateOptionsLoading,
    earTagChangeRef,
    milkKgRef,
    proficiencyTestDateRef,
    stableNumberRef,
    t,
    tCommon,
    proficiencyTestDateDisabled,
    stableNumberDisabled,
    processingForm,
    isUpdateCorrection,
    submitDisabled,
    fatPercentageRef,
    proteinPercentageRef,
    lactosePercentageRef,
    somaticCellsRef,
    ureaRef,
    submitRef,
    onTechnicianNumberChange,
    onEarTagChange,
    onUpdatedEarTagChange,
    onProficiencyTestDateChange,
    onStableNumberChange,
    onCorrectionTypeChange,
    onMilkKgInput,
    onFatPercentageInput,
    onProteinPercentageInput,
    onLactosePercentageInput,
    onSomaticCellsInput,
    onUreaInput,
    isAdditionalFieldDisabled
  } = useNewApcReportFormSetup({
    earTagRef,
    technicianNumberRef,
    onSubmit
  });
  return <Form data-sentry-element="Form" data-sentry-component="ApcReportFormBase" data-sentry-source-file="index.tsx">
            <div className={styles.formContainer}>
                <Fields.StringSelect name="correctionType" label={t('correctionType')} options={correctionTypeOptions} disabled={editingCorrectionReport} isRequired onFieldChange={onCorrectionTypeChange} autoFocus inputRef={correctionTypeRef} clearable data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                <Fields.Input name="technicianNumber" label={t('technicianNumber')} onFieldChange={onTechnicianNumberChange} isRequired inputRef={technicianNumberRef} type="number" data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                <div className={styles.doubleWidth}>
                    <Fields.EarTagInput name="earTag" label={t('earTag')} inputRef={earTagRef} isRequired disabled={editingCorrectionReport} onFieldChange={onEarTagChange} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </div>
                <div className={styles.doubleWidth}>
                    <Fields.StringSelect name="stableNumber" label={t('stableNumber')} isRequired options={stableOptions.orElse([])} fieldIsLoading={stableDateOptionsLoading} searchable disabled={stableNumberDisabled} inputRef={stableNumberRef} onFieldChange={onStableNumberChange} clearable data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </div>
                <div className={styles.doubleWidth}>
                    <Fields.StringSelect name="proficiencyTestDate" label={t('proficiencyTestDate')} isRequired options={proficiencyTestDateOptions.orElse([])} fieldIsLoading={stableDateOptionsLoading} onFieldChange={onProficiencyTestDateChange} inputRef={proficiencyTestDateRef} disabled={proficiencyTestDateDisabled()} searchable clearable data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </div>
                <div className={styles.doubleWidth}>
                    <Fields.EarTagInput name="updatedEarTag" label={t('updatedEarTag')} disabled={correctionType !== 'EAR_TAG_UPDATE'} isRequired={correctionType === 'EAR_TAG_UPDATE'} inputRef={earTagChangeRef} onFieldChange={onUpdatedEarTagChange} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </div>
                <div className={styles.doubleWidth}>
                    <Fields.TripleAttribute name="milkingTripleAttribute" label={t('milkingTripleAttribute')} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </div>
                <Fields.NumberInput name="milkKg" label={t('milkKg')} disabled={!isUpdateCorrection} isRequired inputRef={milkKgRef} precision={decimal52DecimalPlaces} onFieldInput={onMilkKgInput} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                <Fields.NumberInput name="fatPercentage" label={t('fatPercentage')} disabled={isAdditionalFieldDisabled('fatPercentage')} precision={decimal52DecimalPlaces} inputRef={fatPercentageRef} onFieldInput={onFatPercentageInput} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                <Fields.NumberInput name="proteinPercentage" label={t('proteinPercentage')} disabled={isAdditionalFieldDisabled('proteinPercentage')} precision={decimal52DecimalPlaces} inputRef={proteinPercentageRef} onFieldInput={onProteinPercentageInput} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                <Fields.NumberInput name="lactosePercentage" label={t('lactosePercentage')} disabled={isAdditionalFieldDisabled('lactosePercentage')} precision={decimal52DecimalPlaces} inputRef={lactosePercentageRef} onFieldInput={onLactosePercentageInput} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                <Fields.NumberInput name="somaticCells" label={t('somaticCells')} disabled={isAdditionalFieldDisabled('somaticCells')} precision={0} inputRef={somaticCellsRef} onFieldInput={onSomaticCellsInput} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                <Fields.NumberInput name="urea" label={t('urea')} disabled={isAdditionalFieldDisabled('urea')} precision={decimal52DecimalPlaces} inputRef={ureaRef} onFieldInput={onUreaInput} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                <div className={classNames(styles.doubleWidth, styles.buttons)}>
                    <div>
                        <label className="invisible">.</label>
                        <Button onClick={onCancel} data-sentry-element="Button" data-sentry-source-file="index.tsx">{tCommon('cancel')}</Button>
                    </div>
                    <div>
                        <label className="invisible">.</label>
                        <Button type="submit" visuals="primary" loading={processingForm} testId="submit-button" disabled={submitDisabled} ref={submitRef} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                            {tCommon(processingForm ? 'validating' : 'save')}
                        </Button>
                    </div>
                </div>
            </div>
        </Form>;
};

/**
 * Renders the analysis protocol correction form with correction-specific field enabling.
 *
 * @param props Form callbacks and refs required for focus management.
 * @returns The correction report form.
 *
 * @example
 * <ApcReportForm
 *   earTagRef={earTagRef}
 *   technicianNumberRef={technicianNumberRef}
 *   correctionTypeRef={correctionTypeRef}
 *   onSubmit={handleSubmit}
 *   onCancel={handleCancel}
 * />
 */
export const ApcReportForm = memo(ApcReportFormBase);